import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Upload, ShieldCheck, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Section } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export function CodesCard({ b }) {
  const { t } = useT();
  const showReturn = ["picked_up", "washing", "ready"].includes(b.status);
  const pin = showReturn ? b.return_pin : b.pickup_pin;
  const label = showReturn ? t("bk.return_code") : t("bk.pickup_code");
  return (
    <Section title={t("bk.codes")} testId="codes-card">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="p-3 bg-white rounded-xl border border-line"><QRCodeSVG value={`washly:${b.id}:${pin}`} size={148} aria-label={`QR ${label}`} /></div>
        <div>
          <p className="w-label">{label}</p>
          <p className="text-4xl font-extrabold tracking-[0.3em] tabular-nums" data-testid={showReturn ? "return-pin" : "pickup-pin"}>{pin}</p>
          <p className="w-muted mt-2">{t("bk.codes_hint")}</p>
        </div>
      </div>
    </Section>
  );
}

export function FileUpload({ label, onFile, testId, accept = "image/jpeg,image/png,image/webp,application/pdf" }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className={`w-btn w-btn-secondary cursor-pointer ${busy ? "opacity-60" : ""}`}>
      <Upload size={18} aria-hidden="true" /> {busy ? "Lädt hoch…" : label}
      <input type="file" className="sr-only" accept={accept} disabled={busy} data-testid={testId}
        onChange={async (e) => { const f = e.target.files[0]; if (!f) return; setBusy(true); try { await onFile(f); } finally { setBusy(false); e.target.value = ""; } }} />
    </label>
  );
}

export function IdUpload({ b, reload }) {
  const { t } = useT();
  const upload = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post(`/bookings/${b.id}/id-document`, fd); toast.success(t("bk.id_done")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (b.id_doc_file_id) return <p className="font-semibold text-mint-ink flex items-center gap-2" data-testid="id-doc-done"><ShieldCheck size={18} aria-hidden="true" /> {t("bk.id_done")}</p>;
  return (
    <div className="w-card p-4 bg-[var(--w-warn-soft)] space-y-2" data-testid="id-upload-box">
      <p className="font-semibold text-[var(--w-warn)]">{t("bk.id_upload")} – {t("bk.id_required")}</p>
      <p className="w-muted">Verschlüsselt gespeichert, nur für Prüfung durch Washly einsehbar.</p>
      <FileUpload label={t("bk.id_upload")} onFile={upload} testId="id-upload-input" />
    </div>
  );
}

export function ComplaintDialog({ b, reload }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "damage", description: "", amount_claimed: 0 });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/bookings/${b.id}/complaint`, { ...f, amount_claimed: +f.amount_claimed }); toast.success("Reklamation eingereicht"); setOpen(false); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="w-btn w-btn-danger" data-testid="complaint-open-btn">{t("bk.complaint")}</button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("bk.complaint")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="complaint-form">
          <label className="block"><span className="w-label">Art</span>
            <select className="w-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="complaint-type">
              <option value="damage">Schaden (Versicherungsfall)</option><option value="loss">Verlust (Versicherungsfall)</option>
              <option value="quality">Qualität</option><option value="no_show">Nicht erschienen</option><option value="other">Sonstiges</option>
            </select></label>
          <label className="block"><span className="w-label">Beschreibung</span>
            <textarea className="w-input !h-28 py-3" minLength={10} required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="complaint-description" /></label>
          {["damage", "loss"].includes(f.type) && <label className="block"><span className="w-label">Geschätzter Schaden (€)</span>
            <input type="number" min="0" className="w-input" value={f.amount_claimed} onChange={(e) => setF({ ...f, amount_claimed: e.target.value })} data-testid="complaint-amount" /></label>}
          <button className="w-btn w-btn-primary w-full" data-testid="complaint-submit-btn">Einreichen</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewBox({ b, reload }) {
  const { t } = useT();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (b.my_review) return <p className="w-card p-4 font-semibold" data-testid="review-done">{t("bk.review_done")}{b.my_review.status === "flagged" ? " – wird geprüft." : "."}</p>;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/bookings/${b.id}/review`, { rating, text }); toast.success(t("bk.review_done")); reload(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Section title={t("bk.review")} testId="review-box">
      <form onSubmit={submit} className="space-y-3">
        <div className="flex gap-1" role="radiogroup" aria-label="Sterne">
          {[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} role="radio" aria-checked={rating === n} aria-label={`${n} Sterne`} onClick={() => setRating(n)} data-testid={`review-star-${n}`} className="p-1">
              <Star size={30} fill={n <= rating ? "currentColor" : "none"} style={{ color: "#b45309" }} aria-hidden="true" />
            </button>
          ))}
        </div>
        <textarea className="w-input !h-24 py-3" placeholder="Wie war es?" value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} data-testid="review-text" />
        <button className="w-btn w-btn-primary" disabled={busy} data-testid="review-submit-btn">{busy ? "Wird geprüft…" : t("bk.review")}</button>
      </form>
    </Section>
  );
}
